import { t as tr } from './i18n';
import { accessOf, planOf, type AccessState } from './plans';

/**
 * Consola echipei Procumi (/consola, 2026-10-29): tichetele de suport, firmele și
 * abonamentele lor, plățile care lipsesc, echipa și istoricul. Conturile echipei sunt
 * separate de ale clienților; rolul spune ce poate face fiecare.
 */

export type StaffRole = 'owner' | 'billing' | 'support';
export const STAFF_ROLES: readonly StaffRole[] = ['owner', 'billing', 'support'];
export const isStaffRole = (v: unknown): v is StaffRole => STAFF_ROLES.includes(v as StaffRole);

/** Ce se poate face în consolă. Citirea firmelor și a tichetelor e a tuturor. */
export type StaffPermission =
  /** răspunde la tichete, le închide, le dă cuiva */
  | 'tickets'
  /** schimbă pachetul, proba, plata, suspendă */
  | 'companies'
  /** linkuri de înscriere pentru firme noi */
  | 'signups'
  /** invită și scoate oameni din echipă */
  | 'team'
  /** setările Procumi: Atlas în probă, limitele de documente (Ticket 08) */
  | 'settings';

const PERMISSIONS: Record<StaffRole, readonly StaffPermission[]> = {
  owner: ['tickets', 'companies', 'signups', 'team', 'settings'],
  billing: ['tickets', 'companies', 'signups'],
  support: ['tickets'],
};

export const staffCan = (role: string, p: StaffPermission): boolean =>
  isStaffRole(role) && PERMISSIONS[role].includes(p);

export function staffRoleLabel(role: string): string {
  if (role === 'owner') return tr('Proprietar');
  if (role === 'billing') return tr('Vânzări și plăți');
  return tr('Suport');
}

export function staffRoleHint(role: string): string {
  if (role === 'owner') return tr('Tot, inclusiv echipa.');
  if (role === 'billing') return tr('Tichete, firme, pachete, plăți și linkuri de înscriere.');
  return tr('Tichetele. Firmele le vede, dar nu le schimbă.');
}

/* Tichetele */

export type TicketStatus = 'open' | 'pending' | 'solved';
export type TicketCategory = 'question' | 'problem' | 'billing' | 'idea';
export type TicketPriority = 'normal' | 'high';
export const TICKET_CATEGORIES: readonly TicketCategory[] = ['question', 'problem', 'billing', 'idea'];

export function ticketStatusLabel(s: string, forCustomer = false): string {
  if (s === 'open') return forCustomer ? tr('Trimisă') : tr('De răspuns');
  if (s === 'pending') return forCustomer ? tr('Ți-am răspuns') : tr('Așteaptă clientul');
  return tr('Rezolvată');
}

export function ticketCategoryLabel(c: string): string {
  if (c === 'problem') return tr('Ceva nu merge');
  if (c === 'billing') return tr('Pachet și plată');
  if (c === 'idea') return tr('Idee sau cerere');
  return tr('Întrebare');
}

/** Tichetul fără răspuns de atâtea ore e „întârziat” în consolă. */
export const TICKET_LATE_HOURS = 24;

export const TICKET_MAX_FILES = 3;
export const TICKET_MAX_FILE = 5 * 1024 * 1024;
export const TICKET_FILE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp', 'application/pdf']);

/* Firmele, văzute din consolă */

/**
 * Starea comercială a unei firme, într-un cuvânt, pentru filtre și etichete.
 * `past_due`: Stripe n-a putut lua banii; `overdue`: plata ținută de mână („plătit până la”)
 * a trecut; `ending`: proba se termină în cel mult 3 zile.
 */
export type CompanyStanding =
  | 'suspended'
  | 'past_due'
  | 'overdue'
  | 'active'
  | 'ending'
  | 'trial'
  | 'readonly'
  | 'legacy'
  | 'demo';

export const ENDING_DAYS = 3;

export function companyStanding(
  c: {
    isDemo: boolean;
    suspendedAt: string | null;
    plan: string | null;
    trialEndsAt: string | null;
    paidUntil: string | null;
    subscriptionStatus: string | null;
  },
  now: Date = new Date(),
): CompanyStanding {
  if (c.isDemo) return 'demo';
  if (c.suspendedAt) return 'suspended';
  if (c.subscriptionStatus === 'past_due' || c.subscriptionStatus === 'unpaid') return 'past_due';
  const access: AccessState = accessOf(c, now).state;
  if (access === 'active') {
    // plata de mână: ziua din `paidUntil` e încă plătită
    if (c.paidUntil && c.paidUntil < now.toISOString().slice(0, 10)) return 'overdue';
    return 'active';
  }
  if (access === 'trial') {
    const left = Date.parse(c.trialEndsAt!) - now.getTime();
    return left <= ENDING_DAYS * 86_400_000 ? 'ending' : 'trial';
  }
  return access;
}

export function standingLabel(s: CompanyStanding): string {
  switch (s) {
    case 'suspended':
      return tr('Suspendată');
    case 'past_due':
      return tr('Plata cu cardul a eșuat');
    case 'overdue':
      return tr('Plata a expirat');
    case 'active':
      return tr('Plătește');
    case 'ending':
      return tr('Proba se termină');
    case 'trial':
      return tr('În probă');
    case 'readonly':
      return tr('Doar citire');
    case 'legacy':
      return tr('Fără pachet (veche)');
    default:
      return tr('Demonstrație');
  }
}

/** Cât aduce pe lună un pachet, în moneda lui: anualul împărțit la 12. */
export function monthlyValue(plan: string | null, billing: string | null, currency: 'RON' | 'EUR'): number {
  const p = planOf(plan);
  if (!p) return 0;
  if (currency === 'EUR') return billing === 'yearly' ? p.eur.yearly / 12 : p.eur.monthly;
  return billing === 'yearly' ? p.yearly / 12 : p.monthly;
}

/* Ce trimite serverul consolei */

export interface StaffMe {
  id: string;
  email: string;
  name: string;
  role: StaffRole;
  notifyTickets: boolean;
}

export interface StaffMember extends StaffMe {
  /** încă n-a pus parola din invitație */
  invited: boolean;
  active: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface TicketFile {
  index: number;
  filename: string;
  mime: string;
  size: number;
}

export interface TicketMessageView {
  id: string;
  author: 'customer' | 'staff' | 'note';
  /** numele celui care a scris: clientul sau omul din echipă */
  name: string;
  body: string;
  via: 'app' | 'email';
  files: TicketFile[];
  createdAt: string;
}

export interface TicketRow {
  id: string;
  number: number;
  subject: string;
  status: TicketStatus;
  category: TicketCategory;
  priority: TicketPriority;
  email: string;
  name: string;
  company: { id: string; name: string } | null;
  assignee: { id: string; name: string } | null;
  /** ultimul mesaj, scurtat */
  excerpt: string;
  lastCustomerAt: string;
  lastStaffAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TicketDetail extends TicketRow {
  messages: TicketMessageView[];
}

/** Tichetele clientului, din „Ajutor”: fără notițele interne. */
export interface MyTicket {
  id: string;
  number: number;
  subject: string;
  status: TicketStatus;
  category: TicketCategory;
  updatedAt: string;
  messages: TicketMessageView[];
}

export interface ConsoleCompany {
  id: string;
  name: string;
  country: string;
  currency: string;
  isDemo: boolean;
  standing: CompanyStanding;
  plan: string | null;
  billing: string | null;
  /** 'AAAA-LL-ZZ': ultima zi de probă */
  trialEndsAt: string | null;
  paidUntil: string | null;
  subscriptionStatus: string | null;
  cancelAtPeriodEnd: boolean;
  stripeCustomerId: string | null;
  suspendedAt: string | null;
  planRequest: { plan: string; billing: string; at: string } | null;
  users: number;
  rfqs: number;
  orders: number;
  openTickets: number;
  /** ultima intrare a cuiva din firmă */
  lastActiveAt: string | null;
  createdAt: string;
  activity: string;
  buyers: string;
}

export interface ConsoleCompanyDetail extends ConsoleCompany {
  cui: string;
  address: string;
  locale: string;
  aiReads: number;
  aiMonthReads: number;
  members: {
    id: string;
    name: string;
    email: string;
    role: string;
    isAdmin: boolean;
    active: boolean;
    lastLoginAt: string | null;
  }[];
  tickets: TicketRow[];
  notes: { id: string; body: string; staff: string; createdAt: string }[];
  history: StaffActionView[];
}

export interface StaffActionView {
  id: string;
  action: string;
  summary: string;
  staff: string;
  company: { id: string; name: string } | null;
  ticketId: string | null;
  createdAt: string;
}

/** Plățile: ce trebuie urmărit acum. */
export interface ConsolePayments {
  /** cardul a eșuat (Stripe) sau plata de mână a expirat */
  overdue: ConsoleCompany[];
  /** plata de mână expiră în 7 zile */
  dueSoon: ConsoleCompany[];
  /** proba se termină în 3 zile */
  trialsEnding: ConsoleCompany[];
  /** proba s-a terminat fără pachet, în ultimele 30 de zile: de sunat */
  trialsEnded: ConsoleCompany[];
  /** au cerut un pachet din aplicație */
  requests: ConsoleCompany[];
  /** anulate din portal: merg până la `paidUntil` */
  canceling: ConsoleCompany[];
  /** facturile deschise din Stripe; null = Stripe nu e pornit */
  invoices:
    | {
        id: string;
        company: string;
        amount: number;
        currency: string;
        dueDate: string | null;
        url: string | null;
      }[]
    | null;
}

export interface ConsoleOverview {
  /** venitul lunar din pachetele plătite, pe monede */
  mrr: { RON: number; EUR: number };
  paying: number;
  trials: number;
  trialsEnding: number;
  readonly: number;
  overdue: number;
  signups7: number;
  signups30: number;
  openTickets: number;
  lateTickets: number;
  liveVisitors: number;
  /** firmele noi pe ultimele 12 săptămâni, cea mai veche întâi */
  weeks: { week: string; signups: number; paid: number }[];
  recent: StaffActionView[];
}
